'use client';

import { Box, Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import { useMediaBrowserV2 } from '@/app/features/shared/media-browser-v2/context/MediaBrowserV2Context';
import MediaGridV2 from './MediaGridV2';
import MediaDetailsPanel from './MediaDetailsPanel';
import MediaToolbar, { MediaPagination } from './MediaToolbar';
import { useState, useCallback } from 'react';
import { Warning } from '@mui/icons-material';
import { toast } from 'react-toastify';

export default function MediaBrowserV2() {
    const { state, selectedMedia, selectedMediaList, onApply, onClose, uploadPendingFile } = useMediaBrowserV2();
    const [showCloseWarning, setShowCloseWarning] = useState(false);
    const [isApplyingSelection, setIsApplyingSelection] = useState(false);

    const hasPendingUploads = state.pendingUploads.some(
        upload => upload.uploadStatus === 'pending' || upload.uploadStatus === 'uploading'
    );

    const selectedPendingUploads = selectedMediaList.filter(media => 'uploadStatus' in media);
    const hasSelectedPendingUploads = selectedPendingUploads.length > 0;

    const handleApply = useCallback(async () => {
        if (!selectedMedia || !onApply) return;

        if ('documentId' in selectedMedia) {
            onApply(selectedMedia);
            return;
        }

        const selectedPendingId = selectedMedia.id;
        const pendingIdsToUpload = state.pendingUploads
            .filter(upload => upload.uploadStatus === 'pending' || upload.uploadStatus === 'error')
            .map(upload => upload.id);

        const orderedPendingIds = pendingIdsToUpload.includes(selectedPendingId)
            ? [selectedPendingId, ...pendingIdsToUpload.filter(id => id !== selectedPendingId)]
            : pendingIdsToUpload;

        setIsApplyingSelection(true);
        try {
            let selectedUploadedFile = null;

            for (const pendingId of orderedPendingIds) {
                const uploadedFile = await uploadPendingFile(pendingId);
                if (pendingId === selectedPendingId && uploadedFile) {
                    selectedUploadedFile = uploadedFile;
                }
            }

            if (selectedUploadedFile) {
                onApply(selectedUploadedFile);
                return;
            }

            toast.error('Ausgewählte Datei konnte nicht hochgeladen werden');
        } finally {
            setIsApplyingSelection(false);
        }
    }, [selectedMedia, onApply, state.pendingUploads, uploadPendingFile]);

    const handleClose = useCallback(() => {
        if (hasPendingUploads) {
            setShowCloseWarning(true);
        } else if (onClose) {
            onClose();
        }
    }, [hasPendingUploads, onClose]);

    const handleConfirmClose = useCallback(() => {
        setShowCloseWarning(false);
        if (onClose) {
            onClose();
        }
    }, [onClose]);

    const handleCancelClose = useCallback(() => {
        setShowCloseWarning(false);
    }, []);

    const selectedPendingStatus = selectedMedia && 'uploadStatus' in selectedMedia
        ? selectedMedia.uploadStatus
        : null;
    const selectedPendingNeedsUpload = selectedPendingStatus === 'pending' || selectedPendingStatus === 'error';
    const canApply = !!selectedMedia
        && !isApplyingSelection
        && (selectedPendingStatus === null || selectedPendingNeedsUpload);
    const applyLabel = hasSelectedPendingUploads
        ? ((isApplyingSelection || selectedPendingStatus === 'uploading' || selectedPendingStatus === 'success')
            ? 'Lädt hoch...'
            : 'Hochladen & Auswählen')
        : 'Auswählen';

    return (
        <Box className="flex h-full w-full flex-col overflow-hidden">
            {/* Main Content Area */}
            <Box className="flex min-h-0 flex-1 overflow-hidden">
                {/* Left: Grid Area */}
                <Box className="flex min-w-0 flex-1 flex-col overflow-hidden">
                    {/* Toolbar */}
                    <Box className="shrink-0 p-4 pb-2">
                        <MediaToolbar />
                    </Box>

                    {/* Grid */}
                    <Box className="min-h-0 flex-1 overflow-hidden px-4">
                        <MediaGridV2 />
                    </Box>

                    {/* Pagination below grid */}
                    <Box className="shrink-0 px-4 pb-4">
                        <MediaPagination />
                    </Box>
                </Box>

                {/* Right: Details Panel */}
                <MediaDetailsPanel />
            </Box>

            {/* Footer: Actions (only in picker mode) */}
            {state.mode === 'picker' && (
                <Box
                    className="shrink-0 p-4"
                    sx={{
                        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        {onClose && (
                            <Button
                                variant="outlined"
                                color="inherit"
                                onClick={handleClose}
                            >
                                Abbrechen
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!canApply}
                            onClick={handleApply}
                        >
                            {applyLabel}
                        </Button>
                    </Stack>
                </Box>
            )}

            {/* Warning Dialog for pending uploads */}
            <Dialog
                open={showCloseWarning}
                onClose={handleCancelClose}
            >
                <DialogTitle className="flex items-center gap-2">
                    <Warning color="warning" />
                    Unfertige Uploads
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Es sind noch {state.pendingUploads.filter(u => u.uploadStatus === 'pending' || u.uploadStatus === 'uploading').length} Dateien nicht hochgeladen.
                    </Typography>
                    <Typography className="mt-2">
                        Möchten Sie wirklich fortfahren? Nicht hochgeladene Dateien gehen verloren.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelClose} variant="outlined">
                        Zurück
                    </Button>
                    <Button onClick={handleConfirmClose} variant="contained" color="error">
                        Trotzdem schließen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
